import { LSP_MAX_DIAGNOSTICS_PER_FILE, LSP_MAX_DIAGNOSTICS_TOTAL } from "@racler/shared";
import { relativeFromSynthetic, type LspDiagnostic } from "./types.ts";

export interface ProblemItem {
  path: string;
  severity: 1 | 2 | 3 | 4;
  message: string;
  line: number; // 1-based
  column: number; // 1-based
  endLine: number;
  endColumn: number;
  source?: string;
  code?: string;
}

/**
 * The single browser-side source of diagnostics for ONE Project (M6.1). Both the Monaco inline markers and the Problems
 * panel render from it. It only accepts URIs of its own Project (anything else is dropped), caps pathological counts, is
 * replaced wholesale per file by each `publishDiagnostics` and is cleared when the session/Project goes away.
 * Never persisted.
 */
export class DiagnosticsStore {
  private readonly projectId: string;
  private files = new Map<string, LspDiagnostic[]>();
  private readonly listeners = new Set<(changedPath: string | null) => void>();
  private snapshot: ProblemItem[] | null = [];

  constructor(projectId: string) {
    this.projectId = projectId;
  }

  /** Handles `textDocument/publishDiagnostics` params. Returns false when it was ignored. */
  publish(params: unknown): boolean {
    const p = params as { uri?: unknown; diagnostics?: unknown } | null;
    if (!p || typeof p.uri !== "string" || !Array.isArray(p.diagnostics)) return false;
    const path = relativeFromSynthetic(this.projectId, p.uri);
    if (path === null) return false; // another Project, an external placeholder, or junk: never shown
    const list = (p.diagnostics as LspDiagnostic[])
      .filter((d) => d && typeof d.message === "string" && d.range?.start && d.range?.end)
      .slice(0, LSP_MAX_DIAGNOSTICS_PER_FILE);
    if (list.length === 0) this.files.delete(path);
    else this.files.set(path, list);
    this.enforceTotalCap();
    this.changed(path);
    return true;
  }

  forPath(path: string): readonly LspDiagnostic[] {
    return this.files.get(path) ?? [];
  }

  paths(): string[] {
    return [...this.files.keys()];
  }

  clear(): void {
    if (this.files.size === 0) return;
    const paths = this.paths();
    this.files = new Map();
    this.snapshot = null;
    for (const path of paths) for (const l of this.listeners) l(path);
    for (const l of this.listeners) l(null);
  }

  /** Stable between changes (safe for useSyncExternalStore). Sorted: errors first, then by path and position. */
  problems(): ProblemItem[] {
    if (this.snapshot) return this.snapshot;
    const out: ProblemItem[] = [];
    for (const [path, diags] of this.files) {
      for (const d of diags) {
        out.push({
          path,
          severity: d.severity ?? 1,
          message: d.message,
          line: d.range.start.line + 1,
          column: d.range.start.character + 1,
          endLine: d.range.end.line + 1,
          endColumn: d.range.end.character + 1,
          ...(d.source ? { source: d.source } : {}),
          ...(d.code !== undefined ? { code: String(d.code) } : {}),
        });
      }
    }
    out.sort((a, b) => a.severity - b.severity || a.path.localeCompare(b.path) || a.line - b.line || a.column - b.column);
    this.snapshot = out;
    return out;
  }

  subscribe(fn: (changedPath: string | null) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private enforceTotalCap(): void {
    let total = 0;
    for (const list of this.files.values()) total += list.length;
    if (total <= LSP_MAX_DIAGNOSTICS_TOTAL) return;
    // Keep the files that were published first; trim the rest (UI safety, not a correctness feature).
    let budget = LSP_MAX_DIAGNOSTICS_TOTAL;
    for (const [path, list] of this.files) {
      if (budget <= 0) this.files.delete(path);
      else if (list.length > budget) this.files.set(path, list.slice(0, budget));
      budget -= list.length;
    }
  }

  private changed(path: string): void {
    this.snapshot = null;
    for (const l of this.listeners) l(path);
    for (const l of this.listeners) l(null);
  }
}
